import { Controller } from "@hotwired/stimulus"
import consumer from "../channels"

export default class extends Controller {
  connect() {
    const conversationId = this.element.dataset.conversationId
    if (!conversationId) return

    // Keep the view pinned to the newest messages unless the operator has
    // deliberately scrolled up to read history.
    //
    // Sticky is only *cleared* by an upward scroll (scrollTop decreasing).
    // Deciding by distance-to-bottom alone had a race: when an image finished
    // loading, the handler could run after the content grew, read a large
    // distance, and conclude the operator had scrolled away — after which
    // nothing ever re-pinned.
    this.stickToBottom = true
    this.lastScrollTop = 0
    this.onScroll = () => {
      const el = this.element
      if (el.scrollHeight - el.scrollTop - el.clientHeight < 120) {
        this.stickToBottom = true
      } else if (el.scrollTop < this.lastScrollTop - 1) {
        this.stickToBottom = false
      }
      this.lastScrollTop = el.scrollTop
    }
    this.element.addEventListener("scroll", this.onScroll)

    // Re-pin whenever the thread grows, no matter why: images decoding, audio
    // players rendering, fonts, new bubbles. Observing the list's size makes
    // load-event timing irrelevant. RO fires after layout and before paint, so
    // the snap is invisible rather than a visible jump.
    const list = document.getElementById("messages-list")
    if (list && "ResizeObserver" in window) {
      this.resizeObserver = new ResizeObserver(() => {
        if (this.stickToBottom) this.element.scrollTop = this.element.scrollHeight
      })
      this.resizeObserver.observe(list)
    }

    this.sessionExpiresAt = this.element.dataset.sessionExpiresAt
      ? new Date(this.element.dataset.sessionExpiresAt)
      : null

    this.subscription = consumer.subscriptions.create(
      { channel: "ConversationChannel", id: conversationId },
      {
        received: (data) => {
          if (data.type === "new_message") {
            const messagesList = document.getElementById("messages-list")
            // Sending re-renders the whole panel via its Turbo Frame, so the
            // broadcast for our own message can land on a list that already
            // shows it — never append a message id that's already on screen.
            if (messagesList && !this.alreadyRendered(data.html)) {
              messagesList.insertAdjacentHTML("beforeend", data.html)
              // Follow the conversation unless the operator scrolled up to
              // read history — yanking them mid-read loses their place.
              if (this.stickToBottom) this.scrollToBottom()
            }

            if (data.session_active && data.session_expires_at) {
              this.sessionExpiresAt = new Date(data.session_expires_at)
              this.element.dataset.sessionExpiresAt = data.session_expires_at
              this.activateSession()
              this.startTimer()
            }
          } else if (data.type === "status_update") {
            const msg = document.getElementById(`message-${data.communication_id}`)
            if (msg && data.status_html) {
              const statusEl = msg.querySelector("[data-status]")
              if (statusEl) statusEl.innerHTML = data.status_html
            }
          } else if (data.type === "message_edited" || data.type === "message_updated") {
            // message_updated also covers media finishing its download.
            const el = document.getElementById(`message-${data.communication_id}`)
            if (el && data.html) el.outerHTML = data.html
          } else if (data.type === "bot_error") {
            this.showBotError(data.message)
          }
        }
      }
    )

    this.scrollToBottom()
    if (this.sessionExpiresAt && this.sessionExpiresAt > new Date()) {
      this.startTimer()
    }
  }

  alreadyRendered(html) {
    const match = /id="(message-\d+)"/.exec(html || "")
    return !!(match && document.getElementById(match[1]))
  }

  disconnect() {
    if (this.subscription) this.subscription.unsubscribe()
    if (this.timerInterval) clearInterval(this.timerInterval)
    if (this.onScroll) this.element.removeEventListener("scroll", this.onScroll)
    if (this.resizeObserver) this.resizeObserver.disconnect()
  }

  scrollToBottom() {
    // Set immediately (rAF is suspended in hidden tabs, and an ActionCable
    // message can arrive while the operator is on another tab), and again on
    // the next frame for anything that lays out after this tick.
    this.element.scrollTop = this.element.scrollHeight
    requestAnimationFrame(() => {
      this.element.scrollTop = this.element.scrollHeight
    })
  }


  startTimer() {
    if (this.timerInterval) clearInterval(this.timerInterval)
    this.updateTimer()
    this.timerInterval = setInterval(() => this.updateTimer(), 1000)
  }

  updateTimer() {
    if (!this.sessionExpiresAt) return

    const now = new Date()
    const diff = this.sessionExpiresAt - now

    if (diff <= 0) {
      clearInterval(this.timerInterval)
      this.deactivateSession()
      return
    }

    const hours = Math.floor(diff / 3600000)
    const mins = Math.floor((diff % 3600000) / 60000)
    const secs = Math.floor((diff % 60000) / 1000)

    const countdownEl = document.querySelector("[data-session-countdown]")
    if (countdownEl) {
      countdownEl.textContent = `Tiempo restante para mensajes libres: ${hours}hr ${mins}min ${secs}seg`
    }
  }

  // The chat panel always renders both compose states (free-form form and
  // expired/template-only block); these two methods just swap which is visible.
  activateSession() {
    this.toggleSessionUi(true)
  }

  deactivateSession() {
    this.toggleSessionUi(false)
  }

  toggleSessionUi(active) {
    const inputBox = document.querySelector("[data-input-box]")
    if (!inputBox) return

    inputBox.querySelector("[data-session-active-ui]")?.classList.toggle("hidden", !active)
    inputBox.querySelector("[data-session-expired-ui]")?.classList.toggle("hidden", active)

    if (active) inputBox.querySelector("[data-freeform-input] [name='body']")?.focus()
  }

  showBotError(message) {
    // Remove existing error banner
    const existing = document.getElementById("bot-error-banner")
    if (existing) existing.remove()

    const container = this.element.parentElement
    const banner = document.createElement("div")
    banner.id = "bot-error-banner"
    banner.className = "mx-4 mb-2 px-4 py-3 rounded-lg bg-red-50 border border-red-200 flex items-start gap-3"
    banner.innerHTML = `
      <svg class="w-5 h-5 text-red-500 flex-shrink-0 mt-0.5" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke="currentColor">
        <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.964-.833-2.732 0L4.082 16.5c-.77.833.192 2.5 1.732 2.5z" />
      </svg>
      <div class="flex-1">
        <p class="text-sm font-medium text-red-800">${message}</p>
      </div>
      <button onclick="this.closest('#bot-error-banner').remove()" class="text-red-400 hover:text-red-600">
        <svg class="w-4 h-4" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke="currentColor">
          <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M6 18L18 6M6 6l12 12" />
        </svg>
      </button>
    `

    // Insert before the input box
    const inputBox = container.querySelector("[data-input-box]")
    if (inputBox) {
      inputBox.parentElement.insertBefore(banner, inputBox)
    } else {
      container.appendChild(banner)
    }
  }
}
